import { graphql, useStaticQuery } from 'gatsby';
import { FC } from 'react';
import { useMeasure } from 'react-use';

import Icon from 'atoms/Icons/Icon';
import color from 'atoms/colors/colors';

// Quarks
import Flex from 'quarks/Flex';
import Heading from 'quarks/Heading';
import Image, { ImageData } from 'quarks/Image';
import Link from 'quarks/Link';
import Paragraph from 'quarks/Paragraph';
import Text from 'quarks/Text';
import type { FlexProps } from 'quarks/interpolations/flex';

import Dots from 'molecules/Pagination/Dots';

import ComponentButton from 'components/Button/ComponentButton';

import richTextParser from 'utils/richTextParser';
import useSwipeable from 'utils/useSwipeable';

// Types
import type { ContentfulComponentCardDeck, ContentfulTemplateBlogConnection } from 'graphqlTypes';

interface ResourceSliderProps extends FlexProps, ContentfulComponentCardDeck {
  backgroundIsDark: boolean;
}

interface BlogData {
  blogs: ContentfulTemplateBlogConnection;
}

const headingWrapCSS = `
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const textWrapCSS = `
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ComponentResourceSlider: FC<ResourceSliderProps> = ({ backgroundIsDark }) => {
  const { blogs } = useStaticQuery<BlogData>(graphql`
    query getRecentResourcesQuery {
      blogs: allContentfulTemplateBlog(
        sort: { fields: publishDate, order: DESC }
        limit: 4
        filter: { blogType: { eq: "Blog" } }
      ) {
        nodes {
          ...contentfulTemplateBlog
        }
      }
    }
  `);

  const length = blogs?.nodes?.length || 0;
  const resources = blogs?.nodes;
  const [ref, { width: cardWidth }] = useMeasure<HTMLDivElement>();
  const [activeIndex, setActive, cardContainerRef] = useSwipeable(length);

  return (
    <Flex flexDirection="column" gap="48px" marginBottom={96}>
      <Flex flexDirection="column" gap="16px" md={{ gap: '32px' }}>
        {resources && (
          <Flex
            gap="30px"
            justifyContent="flex-start"
            transform={`translateX(-${activeIndex * cardWidth + activeIndex * 30}px)`}
            transition="transform 400ms cubic-bezier(0.215, 0.610, 0.355, 1.000)"
            minWidth={`calc(100% * ${length} + (30px * ${length - 1}))`}
            ref={cardContainerRef}
          >
            {resources.map(
              item =>
                item && (
                  <Flex
                    key={item?.id}
                    ref={ref}
                    width="100%"
                    height="100%"
                    maxHeight="fit-content"
                    maxWidth="100%"
                    borderRadius="16px"
                    md={{ maxHeight: '474px' }}
                  >
                    <Link href={`/blog/${item?.slug}`} textDecoration="none">
                      <Flex
                        flexDirection="column"
                        justifyContent="space-between"
                        borderRadius="16px"
                        overflowX="hidden"
                        overflowY="hidden"
                        backgroundColor="common-white"
                        boxShadow="xl"
                        height="100%"
                        maxHeight="max-content"
                        minHeight="max-content"
                        md={{ flexDirection: 'row', maxHeight: '340px', minHeight: '340px' }}
                      >
                        {item?.featuredImage && (
                          <Flex width="100%" lg={{ width: '50%' }} xl={{ width: '680px' }}>
                            <Image
                              image={item?.featuredImage as ImageData}
                              alt={item?.title || ''}
                              width="100%"
                              height="100%"
                              objectFit="cover"
                              css={{
                                backdropFilter: backgroundIsDark ? 'blur(10px)' : 'blur(15px)',
                              }}
                            />
                          </Flex>
                        )}
                        <Flex
                          flexDirection="column"
                          width="100%"
                          maxWidth="100%"
                          gap="16px"
                          paddingAll={24}
                          lg={{ maxWidth: '50%', paddingY: 32, paddingX: 40 }}
                          xl={{ maxWidth: '490px' }}
                          css={`
                            @media (min-width: 992px) {
                              button {
                                position: absolute;
                                bottom: 20px;
                              }
                            }
                          `}
                        >
                          {item?.category && (
                            <Flex
                              backgroundColor="blue-25"
                              width="fit-content"
                              paddingY={4}
                              paddingX={12}
                              borderRadius="16px"
                            >
                              <Text textStyle="textXs" fontWeight="semiBold" textColor="blue-900">
                                {item.category}
                              </Text>
                            </Flex>
                          )}
                          {item?.title && (
                            <Heading
                              as="h4"
                              textStyle="h4"
                              textColor="gray-900"
                              css={headingWrapCSS}
                              md={{ textStyle: 'h3' }}
                            >
                              {item.title}
                            </Heading>
                          )}
                          {item?.excerpt && (
                            <Paragraph
                              fontSize="textSm"
                              lineHeight="textSm"
                              textColor="gray-700"
                              marginBottom={0}
                              css={textWrapCSS}
                            >
                              {richTextParser(item.excerpt)}
                            </Paragraph>
                          )}
                          <ComponentButton
                            label="Learn More"
                            endIcon="Arrow Right"
                            buttonColor="Gray 900 Link"
                            link={`/blog/${item?.slug}`}
                            paddingAll={0}
                            css={{
                              '&.MuiButton-root': {
                                width: 'fit-content !important',
                                maxWidth: '100% !important',
                              },
                            }}
                          />
                        </Flex>
                      </Flex>
                    </Link>
                  </Flex>
                ),
            )}
          </Flex>
        )}
        {resources && resources.length > 1 && (
          <Flex width="100%" justifyContent="space-between" gap="16px" alignItems="center">
            <Flex gap="16px">
              {[...Array(length).keys()]?.map((_, index: number) =>
                index === activeIndex ? (
                  <Dots isActive color="gray-900" onClick={() => setActive(index)} key={`active-dot-${index}`} />
                ) : (
                  <Dots
                    color="navy-50"
                    onKeyDown={e => e.key === 'Enter' && setActive(index)}
                    onClick={() => setActive(index)}
                    key={`inactive-dot-${index}`}
                  />
                ),
              )}
            </Flex>
            <Flex gap="12px" justifyContent="flex-end">
              <Flex
                borderRadius="6px"
                backgroundColor={backgroundIsDark ? 'common-transparent' : 'navy-50'}
                width="36px"
                height="36px"
                alignItems="center"
                justifyContent="center"
                cursor="pointer"
                border="1px solid"
                borderColor={backgroundIsDark ? 'common-white' : 'navy-50'}
                onClick={() => (activeIndex === 0 ? setActive(length - 1) : setActive(activeIndex - 1))}
              >
                <Icon id="arrow-left" fill={backgroundIsDark ? color.common.white : color.navy[900]} size={16} />
              </Flex>
              <Flex
                borderRadius="6px"
                backgroundColor={backgroundIsDark ? 'common-transparent' : 'navy-50'}
                width="36px"
                height="36px"
                alignItems="center"
                justifyContent="center"
                cursor="pointer"
                border="1px solid"
                borderColor={backgroundIsDark ? 'common-white' : 'navy-50'}
                onClick={() => (activeIndex === length - 1 ? setActive(0) : setActive(activeIndex + 1))}
              >
                <Icon id="arrow-right" fill={backgroundIsDark ? color.common.white : color.navy[900]} size={16} />
              </Flex>
            </Flex>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

export default ComponentResourceSlider;
